import AddIcon from '@mui/icons-material/Add';
import { Button, Divider, Stack } from '@mui/material';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  ConfirmDialog,
  getQuery,
  QUERY_NAME_BY_ENTITY,
  useDialogs,
  useGroups,
} from '@work4all/components';
import {
  DraggableTree,
  TreeNode,
} from '@work4all/components/lib/dataDisplay/tree';
import {
  createTreeData,
  findNodeBy,
  insertNode,
  mapTreeToGroups,
  reindexTree,
  removeNodeBy,
  replaceNodeBy,
} from '@work4all/components/lib/dataDisplay/tree/utils';

import { Group } from '@work4all/models/lib/Classes/Group.entity';
import { InputGroup } from '@work4all/models/lib/Classes/InputGroup.entity';

import { NavigationOverlayHeader } from '../../navigation-overlay-header/NavigationOverlayHeader';
import { useOrganisationAreaContext } from '../hooks';
import { useMutateGroups } from '../hooks/use-mutate-groups';
import { entityToEntityGroup } from '../utils/section-to-group-type';

import { OrganisationAreaGroupsNewDialog } from './organisation-area-groups-new-dialog';

type OrganisationAreaGroupsPageProps = {
  filteredColumns: { id: number }[] | null;
  handleGroupsChange: (groups: Group[]) => void;
  setIsDirty: (state: boolean) => void;
};

export const OrganisationAreaGroupsPage: React.FC<
  OrganisationAreaGroupsPageProps
> = ({ filteredColumns, handleGroupsChange, setIsDirty }) => {
  const { t } = useTranslation();

  const { entity, setGroupsEditOverlayOpen } = useOrganisationAreaContext();

  const groupEntity = entityToEntityGroup(entity);
  const response = useGroups(groupEntity);
  const groups = response.data?.groups ?? null;

  const { treeData } = useMemo(() => createTreeData(groups), [groups]);
  const reIndexedTreeData = useMemo(() => reindexTree(treeData), [treeData]);

  const [draftTree, setDraftTree] = useState<TreeNode[]>([]);

  const isDirty = draftTree?.length > 0;
  const tree =
    !isDirty && reIndexedTreeData.length > 0 ? reIndexedTreeData : draftTree;
  const {
    mutateGroups,
    type: mutationType,
    isDraggable,
    maxDepth,
  } = useMutateGroups(groupEntity, groups);

  const [isRemoveGroupDialogOpen, setRemoveGroupDialogOpen] =
    useState<boolean>(false);

  const [groupIdToRemove, setGroupIdToRemove] = useState<{
    id: string;
    index: number;
    isParent: boolean;
  } | null>(null);
  const dialogs = useDialogs();

  const [selected, setSelected] = useState(null);

  const handleAddEditGroupDialog = (index?: number, addNew?: boolean) => {
    const group =
      index != null && !addNew ? findNodeBy(tree, index, 'index') : undefined;
    const endIndex = draftTree[draftTree?.length - 1]?.index + 1;
    const { id } = dialogs.open(OrganisationAreaGroupsNewDialog, {
      entity,
      group,
      usedIds: draftTree
        .filter((item) => Number(item.id) < 0)
        .map((item) => Number(item.id)),
      endIndex,
      onClose: () => dialogs.close(id),
      onConfirm: async (newGroup) => {
        let newTree = [];
        if (addNew && selected) {
          newTree = insertNode(
            tree,
            newGroup,
            selected?.id,
            selected?.index,
            false
          );
        } else {
          newTree =
            index != null
              ? replaceNodeBy(tree, newGroup, 'index')
              : [...tree, newGroup];
        }

        setDraftTree(reindexTree(newTree));
        setIsDirty(true);
        dialogs.close(id);
      },
    });
  };

  const handleRemoveGroupDialog = (
    id: string,
    index: number,
    isParent: boolean
  ) => {
    setGroupIdToRemove({ id, index, isParent });
    setRemoveGroupDialogOpen(true);
  };

  const handleRemoveGroupCancel = () => {
    setGroupIdToRemove(null);
    setRemoveGroupDialogOpen(false);
  };

  const handleRemoveGroup = () => {
    const updatedDraftState = reindexTree(
      removeNodeBy(tree, groupIdToRemove.index, 'index')
    );

    setDraftTree(updatedDraftState);
    setGroupIdToRemove(null);
    setRemoveGroupDialogOpen(false);
    setIsDirty(true);
  };

  const handleTreeChange = (newTreeData: TreeNode[]) => {
    setDraftTree([...newTreeData]);
    setIsDirty(true);
  };

  const handleItemClick = (group: TreeNode) => {
    setSelected(group.id === selected?.id ? null : group);
  };

  const handleSubmit = async () => {
    const queryName = QUERY_NAME_BY_ENTITY[groupEntity];
    const refetchQuery = getQuery(groupEntity, queryName);

    const structure = mapTreeToGroups(reindexTree(tree));

    const newGroupsResponse = await mutateGroups({
      variables: {
        input: {
          structure,
        },
      },
      refetchQueries: [{ query: refetchQuery }],
    });
    const newGroups: InputGroup[] = newGroupsResponse?.data?.[mutationType];
    const filteredGroups = newGroups.filter(
      (group) => !!filteredColumns?.find(({ id }) => group.id === id)
    );

    handleGroupsChange(filteredGroups);
    setIsDirty(false);
    setGroupsEditOverlayOpen(false);
  };

  return (
    <Stack>
      <NavigationOverlayHeader
        title={t(`MASK.GROUP_MANAGEMENT.${entity.toUpperCase()}_GROUPS.HEADER`)}
        breadcrumbsChildren={
          <Button
            size="large"
            color="primary"
            variant="contained"
            disabled={!isDirty}
            onClick={handleSubmit}
          >
            {t('INPUTS.SAVE')}
          </Button>
        }
      />

      <Stack flexDirection="row" alignItems="center" px="1rem" py={1}>
        <Button
          onClick={() => handleAddEditGroupDialog(null, true)}
          startIcon={<AddIcon />}
        >
          {t('MASK.GROUP_MANAGEMENT.NEW')}
        </Button>
      </Stack>

      <Divider />

      <Stack px="1rem" py={1} gap={1}>
        <DraggableTree
          tree={tree}
          selected={selected}
          isDraggable={isDraggable}
          isSelectable={isDraggable}
          onChange={handleTreeChange}
          onEdit={handleAddEditGroupDialog}
          onRemove={handleRemoveGroupDialog}
          onItemClick={handleItemClick}
          maxDepth={maxDepth}
        />
      </Stack>

      <ConfirmDialog
        open={isRemoveGroupDialogOpen}
        title={t(
          `MASK.GROUP_MANAGEMENT.${entity.toUpperCase()}_GROUPS.REMOVE.HEADER`
        )}
        description={
          groupIdToRemove?.isParent
            ? t('MASK.GROUP_MANAGEMENT.REMOVE.DESCRIPTION_SUBGROUP')
            : t('MASK.GROUP_MANAGEMENT.REMOVE.DESCRIPTION_GROUP')
        }
        confirmLabel={t('ALERTS.YES')}
        cancelLabel={t('ALERTS.NO')}
        onConfirm={handleRemoveGroup}
        onCancel={handleRemoveGroupCancel}
      />
    </Stack>
  );
};
